/**
 * This mixin uses Vue 3 Composition API features (computed, unref) because
 * reactive provide/inject has no Options-API-only equivalent. This is not a
 * pattern to replicate — prefer Options API for new code; Composition API here
 * is a narrow exception forced by the provide/inject constraint.
 * The mixin exists because the components it serves are Options API and
 * converting them is outside the scope of this work.
 */
/* eslint-disable internal-rules/composable-consistent-naming -- mixin, not a composable; see comment above */
import { computed, unref } from 'vue';
import flatten from 'lodash/flatten';
import isEqual from 'lodash/isEqual';
import { mapState as mapPiniaState } from 'pinia';
import { useAskPendoStore } from '@/stores/ask-pendo.store.js';

const LEO_CONTEXT_KEY = Symbol.for('leoContext');

/**
 * Mixin that wires up leo context injection and provision.
 *
 * - Components that define a `leoContext()` option automatically
 *   inject ancestor contexts, append their own, and re-provide the
 *   composed array for descendants.
 * - All components that include this mixin get a `leoContextChain`
 *   computed (the full ancestor-first array).
 * - Components that define a `leoContext()` option also get an
 *   `isActiveLeoContext` computed that returns true when the active
 *   conversation's context matches this component's full context chain.
 */
export const leoContext = {
    inject: {
        __leoAncestorCtx__: {
            from: LEO_CONTEXT_KEY,
            default: undefined,
        },
    },
    computed: {
        ...mapPiniaState(useAskPendoStore, { __leoGetConversationContext__: 'getConversationContext' }),
        leoContextChain () {
            return this.__leoAncestorCtx__
                ? unref(this.__leoAncestorCtx__)
                : [];
        },
        isActiveLeoContext () {
            if (!this.$options.leoContext) return false;

            const activeContext = this.__leoGetConversationContext__(this.$route)?.leoContext;
            if (!activeContext) return false;

            const ownContext = this.$options.leoContext.call(this);
            const fullContext = [...this.leoContextChain, ...flatten([ownContext])];

            return isEqual(activeContext, fullContext);
        },
    },
    provide () {
        if (!this.$options.leoContext) return {};

        const allContexts = computed(() => {
            const prev = this.leoContextChain;
            const cur = this.$options.leoContext.call(this);

            return flatten([...prev, cur]);
        });

        return { [LEO_CONTEXT_KEY]: allContexts };
    },
};
